/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/core';
import Card, { CardBody } from '@airwallex/card';
import { Text } from '@airwallex/typography';
import React, { useContext, useMemo } from 'react';
import { VStack } from '@airwallex/stacks';
// removed unused button and glyph imports
import BillingInfo from './BillingInfo';
import InvoiceLineItemsV1 from './LineItems/LineItems';
import InvoiceLineItemsV2 from './LineItems/LineItemsV2';
import InvoiceTotalSectionV1 from './TotalSection/TotalSection';
import InvoiceTotalSectionV2 from './TotalSection/TotalSectionV2';
import { useTranslation } from 'react-i18next';
import { useMobile } from '@/hooks/useMediaQuery';
import { StoreContext } from '@/providers/storeProvider';
// removed unused imports from previous implementation
import { Accordion } from '@airwallex/accordion';
import CreditNoteInfo from '../CreditNote/CreditNoteInfo';
import CreditNoteLineItemsV1 from '../CreditNote/LineItems/LineItems';
import CreditNoteLineItemsV2 from '../CreditNote/LineItems/LineItemsV2';
import CreditNoteTotalSectionV1 from '../CreditNote/TotalSection/TotalSection';
import CreditNoteTotalSectionV2 from '../CreditNote/TotalSection/TotalSectionV2';
import { useDigitalInvoiceTaxFeature } from '@/hooks/useDigitalInvoiceTaxFeature';

const HeaderText = ({ title, subtitle }: { title: string; subtitle: string }) => {
  return (
    // avoid invalid `<div>` inside `<p>`
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Text as="span" variant="headline400" color="primary">
        {title}
      </Text>
      <Text as="span" variant="body" color="primary">
        {subtitle}
      </Text>
    </span>
  );
};

const InvoiceDetails: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, creditNotes } = invoiceData;
  const { number } = invoice;
  const isMobile = useMobile();
  const digitalInvoiceTax = useDigitalInvoiceTaxFeature();
  const paddingHorizontal = useMemo(() => (isMobile ? 24 : 40), [isMobile]);

  const InvoiceLineItems = digitalInvoiceTax ? InvoiceLineItemsV2 : InvoiceLineItemsV1;
  const InvoiceTotalSection = digitalInvoiceTax ? InvoiceTotalSectionV2 : InvoiceTotalSectionV1;

  return (
    <Card style={{ width: '100%' }}>
      <CardBody style={{ padding: '0' }}>
        <Accordion isHeaderClickable={true} defaultExpandedKeys={isMobile ? [] : ['invoice']} css={{ border: 'none' }}>
          {!isMobile ? (
            <Accordion.Item itemKey="invoice" style={{ padding: `8px ${paddingHorizontal}px` }}>
              <Accordion.Header style={{ padding: '8px 0' }} css={{ '&:hover': { backgroundColor: 'transparent' } }}>
                <HeaderText title={t('invoice')} subtitle={number} />
              </Accordion.Header>
              <Accordion.Content>
                <VStack align="STRETCH" style={{ margin: '0 0 24px 0' }} space="16px">
                  <BillingInfo />
                  <InvoiceLineItems />
                  <InvoiceTotalSection />
                </VStack>
              </Accordion.Content>
            </Accordion.Item>
          ) : (
            <Accordion.Item itemKey="invoice" style={{ padding: `0 ${paddingHorizontal}px 16px` }}>
              <Accordion.Header
                style={{
                  padding: '16px 0 0 0',
                  flexDirection: 'column-reverse',
                  WebkitTapHighlightColor: 'transparent',
                  gap: '8px',
                }}
                css={{
                  '& > div:last-child': { marginLeft: '0', width: '100%', justifyContent: 'space-between' },
                  '&:hover': { backgroundColor: 'transparent' },
                }}
                extra={<HeaderText title={t('invoice')} subtitle={number} />}
              >
                <BillingInfo />
              </Accordion.Header>
              <Accordion.Content>
                <VStack align="STRETCH" style={{ padding: '8px 0' }} space="16px">
                  <InvoiceLineItems />
                  <InvoiceTotalSection />
                </VStack>
              </Accordion.Content>
            </Accordion.Item>
          )}
          {creditNotes?.map((creditNote) => (
            <Accordion.Item key={creditNote.id} itemKey={creditNote.id} style={{ padding: `0 ${paddingHorizontal}px` }}>
              <Accordion.Header
                style={{ padding: '16px 0', WebkitTapHighlightColor: 'transparent' }}
                css={{ '&:hover': { backgroundColor: 'transparent' } }}
              >
                <HeaderText title={t('creditNote.title')} subtitle={creditNote.number} />
              </Accordion.Header>
              <Accordion.Content>
                <VStack align="STRETCH" style={{ paddingBottom: '32px' }} space="16px">
                  <CreditNoteInfo creditNote={creditNote} />
                  {digitalInvoiceTax ? (
                    <>
                      <CreditNoteLineItemsV2 creditNote={creditNote} />
                      <CreditNoteTotalSectionV2 creditNote={creditNote} />
                    </>
                  ) : (
                    <>
                      <CreditNoteLineItemsV1 lineItemGroups={creditNote.lineItems} />
                      <CreditNoteTotalSectionV1 creditNote={creditNote} />
                    </>
                  )}
                </VStack>
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion>
      </CardBody>
    </Card>
  );
};

export default InvoiceDetails;
